import { useQuery } from "@apollo/client";
import { useAuthState } from "@/contexts/auth/auth.provider";
import { GET_KEVEL_OPTIONS } from "@/graphql/queries/info";
import { usePath } from "@/contexts/path/path";
import { useState, useEffect, useRef, useMemo } from "react";

const getAdTypeIds = (adTypes, adTypeObj) => {
  return adTypes.map((type) => adTypeObj[type]?.id).filter(Boolean);
};

const buildKevelRequestBody = ({ kevelOptions, adTypes, userId, keywords }) => {
  const divName = kevelOptions.div_name;
  const networkId = kevelOptions.network_id;

  return {
    enableBotFiltering: true,
    placements: [
      {
        divName,
        networkId,
        siteId: kevelOptions?.site_ids.web,
        adTypes: getAdTypeIds(adTypes, kevelOptions.ad_types),
      },
    ],
    ...(userId ? { user: { user_id: userId } } : {}),
    ...(keywords ? { keywords } : {}),
  };
};

const usePubDecision = ({ listingId, adTypes }) => {
  const { user } = useAuthState();
  const { category, city, direction, district } = usePath();
  const [decisionResult, setDecisionResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [divName, setDivName] = useState(null);

  const fetchedRef = useRef(false);

  const { data, loading, error } = useQuery(GET_KEVEL_OPTIONS);

  const env = process.env.NEXT_PUBLIC_APP_LINK?.includes("sa") ? "pro" : "dev";

  const { kevelOptions, envOptions } = useMemo(() => {
    let kevelOpts = null;
    if (data?.Info?.configurations?.kevel_options) {
      try {
        kevelOpts = JSON.parse(data.Info.configurations.kevel_options);
      } catch (e) {
        kevelOpts = null;
      }
    }

    return {
      kevelOptions: kevelOpts,
      envOptions: kevelOpts?.[env],
    };
  }, [data?.Info?.configurations?.kevel_options, env]);

  useEffect(() => {
    if (!kevelOptions || !envOptions || fetchedRef.current) return;

    let keywords;
    if (listingId) {
      keywords = [
        category?.name,
        city?.name,
        district?.name,
        category?.name?.split(" ")[0],
        category?.name?.split(" ")[1],
      ].filter(Boolean);
    }

    const requestBody = buildKevelRequestBody({
      kevelOptions: envOptions,
      adTypes,
      userId: user?.user_id || null,
      keywords,
    });

    setDivName(envOptions.div_name);

    const fetchDecision = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(envOptions.decision_url, {
          method: "POST",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        });

        const result = await response.json();
        setDecisionResult(result);
        fetchedRef.current = true;
      } catch (err) {
        console.error("Error fetching decision:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDecision();
  }, [kevelOptions, envOptions]); // Simplified dependency array

  const isKevelLoading = loading || isLoading;
  const kevelError = error
    ? error
    : !kevelOptions || !envOptions
    ? "Kevel options not found"
    : null;

  return {
    data: decisionResult,
    decision: decisionResult?.decisions?.[divName],
    loading: isKevelLoading,
    error: kevelError,
    divName,
  };
};

export default usePubDecision;
